import {
  sdkMemberLoaders,
  type RuntimeLoad,
  type SdkMemberName
} from '@Pages/pageRuntime/sdkMemberLoaders';

import { Image } from './Image';
import { BarChart, LineChart, PieChart } from './LazyCharts';
import { lazyModuleComponent } from './lazyComponent';
import type { PrimitiveComponentContracts } from './primitiveComponentContracts';
import { Step, Stepper } from './Stepper';
import { Button as UiButton } from './ui/Button';
import { Spinner as UiSpinner } from './ui/Spinner';

export const preloadAiAgentButton = () => sdkMemberLoaders['Components.AiAgentButton'].preload();

const Accordion = lazyModuleComponent(
  sdkMemberLoaders['Components.Accordion'],
  (module) => module.Accordion
);

const AdminNavButtons = lazyModuleComponent(
  sdkMemberLoaders['Components.AdminNavButtons'],
  (module) => module.AdminNavButtons
);

const AiAgentButton = lazyModuleComponent(
  sdkMemberLoaders['Components.AiAgentButton'],
  (module) => module.AiAgentButton
);

const Alert = lazyModuleComponent(sdkMemberLoaders['Components.Alert'], (module) => module.Alert);

const AnalyticsEmbed = lazyModuleComponent(
  sdkMemberLoaders['Components.AnalyticsEmbed'],
  (module) => module.AnalyticsEmbed
);

const Avatar = lazyModuleComponent(
  sdkMemberLoaders['Components.Avatar'],
  (module) => module.Avatar
);

const Badge = lazyModuleComponent(
  sdkMemberLoaders['Components.Badge'],
  (module): PrimitiveComponentContracts['Badge'] => module.Badge
);

const Breadcrumb = lazyModuleComponent(
  sdkMemberLoaders['Components.Breadcrumb'],
  (module) => module.Breadcrumb
);

const ButtonLink = lazyModuleComponent(
  sdkMemberLoaders['Components.ButtonLink'],
  (module) => module.ButtonLink
);

const Calendar = lazyModuleComponent(
  sdkMemberLoaders['Components.Calendar'],
  (module) => module.CalendarView as PrimitiveComponentContracts['Calendar']
);

const CalendarConnections = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarConnections'],
  (module) => module.CalendarConnections
);

const CalendarNextButton = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarNextButton'],
  (module) => module.CalendarNextButton
);

const CalendarPrevButton = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarPrevButton'],
  (module) => module.CalendarPrevButton
);

const CalendarSelector = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarSelector'],
  (module) => module.CalendarSelector
);

const CalendarTitle = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarTitle'],
  (module) => module.CalendarTitle
);

const CalendarTodayButton = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarTodayButton'],
  (module) => module.CalendarTodayButton
);

const CalendarViewSwitcher = lazyModuleComponent(
  sdkMemberLoaders['Components.CalendarViewSwitcher'],
  (module) => module.CalendarViewSwitcher
);

const Card = lazyModuleComponent(sdkMemberLoaders['Components.Card'], (module) => module.Card);

const Collapsible = lazyModuleComponent(
  sdkMemberLoaders['Components.Collapsible'],
  (module) => module.Collapsible
);

const ConfirmDialog = lazyModuleComponent(
  sdkMemberLoaders['Components.ConfirmDialog'],
  (module) => module.ConfirmDialog
);

const CreateResourceRecordForm = lazyModuleComponent(
  sdkMemberLoaders['Components.CreateResourceRecordForm'],
  (module) => module.CreateResourceRecordForm
);

const DataTable = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTable'],
  (module) => module.DataTable
);

const DataTableAddFilter = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableAddFilter'],
  (module) => module.DataTableAddFilter
);

const DataTableBulkActions = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableBulkActions'],
  (module): PrimitiveComponentContracts['DataTableBulkActions'] => module.DataTableBulkActions
);

const DataTableColumnToggle = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableColumnToggle'],
  (module): PrimitiveComponentContracts['DataTableColumnToggle'] => module.DataTableColumnToggle
);

const DataTableContent = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableContent'],
  (module): PrimitiveComponentContracts['DataTableContent'] => module.DataTableContent
);

const DataTableFilterChips = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableFilterChips'],
  (module) => module.DataTableFilterChips
);

const DataTableFilters = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableFilters'],
  (module) => module.DataTableFilters
);

const DataTablePagination = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTablePagination'],
  (module): PrimitiveComponentContracts['DataTablePagination'] => module.DataTablePagination
);

const DataTableRowActions = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableRowActions'],
  (module): PrimitiveComponentContracts['DataTableRowActions'] => module.DataTableRowActions
);

const DataTableSearch = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableSearch'],
  (module): PrimitiveComponentContracts['DataTableSearch'] => module.DataTableSearch
);

const DataTableToolbar = lazyModuleComponent(
  sdkMemberLoaders['Components.DataTableToolbar'],
  (module) => module.DataTableToolbar as PrimitiveComponentContracts['DataTableToolbar']
);

const DeleteResourceRecord = lazyModuleComponent(
  sdkMemberLoaders['Components.DeleteResourceRecord'],
  (module) => module.DeleteResourceRecord
);

const DropdownMenu = lazyModuleComponent(
  sdkMemberLoaders['Components.DropdownMenu'],
  (module) => module.DropdownMenu
);

const EmailComposer = lazyModuleComponent(
  sdkMemberLoaders['Components.EmailComposer'],
  (module) => module.EmailComposer
);

const EmailMessages = lazyModuleComponent(
  sdkMemberLoaders['Components.EmailMessages'],
  (module) => module.EmailMessages
);

const EmptyState = lazyModuleComponent(
  sdkMemberLoaders['Components.EmptyState'],
  (module) => module.EmptyState
);

const ErrorBoundary = lazyModuleComponent(
  sdkMemberLoaders['Components.ErrorBoundary'],
  (module) => module.ErrorBoundary
);

const ForgotPasswordForm = lazyModuleComponent(
  sdkMemberLoaders['Components.ForgotPasswordForm'],
  (module) => module.ForgotPasswordForm
);

const FormCheckbox = lazyModuleComponent(
  sdkMemberLoaders['Components.FormCheckbox'],
  (module) => module.FormCheckbox
);

const FormCheckboxGroupInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormCheckboxGroupInput'],
  (module) => module.FormCheckboxGroupInput
);

const FormCodeInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormCodeInput'],
  (module) => module.FormCodeInput
);

const FormColorInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormColorInput'],
  (module) => module.FormColorInput as PrimitiveComponentContracts['FormColorInput']
);

const FormComboboxInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormComboboxInput'],
  (module) => module.FormComboboxInput
);

const FormContentInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormContentInput'],
  (module) => module.FormContentInput
);

const FormDateInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormDateInput'],
  (module) => module.FormDateInput as PrimitiveComponentContracts['FormDateInput']
);

const FormDatetimeInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormDatetimeInput'],
  (module) => module.FormDatetimeInput as PrimitiveComponentContracts['FormDatetimeInput']
);

const FormFieldDisplay = lazyModuleComponent(
  sdkMemberLoaders['Components.FormFieldDisplay'],
  (module) => module.FormFieldDisplay
);

const FormFieldInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormFieldInput'],
  (module) => module.FormFieldInput
);

const FormFileUpload = lazyModuleComponent(
  sdkMemberLoaders['Components.FormFileUpload'],
  (module) => module.FormFileInput as PrimitiveComponentContracts['FormFileUpload']
);

const FormLocationInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormLocationInput'],
  (module) => module.FormLocationInput as PrimitiveComponentContracts['FormLocationInput']
);

const FormMarkdownInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormMarkdownInput'],
  (module) => module.FormMarkdownInput
);

const FormMoneyInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormMoneyInput'],
  (module) => module.FormMoneyInput as PrimitiveComponentContracts['FormMoneyInput']
);

const FormMultiComboboxInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormMultiComboboxInput'],
  (module) => module.FormMultiComboboxInput
);

const FormNumberInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormNumberInput'],
  (module) => module.FormNumberInput
);

const FormPasswordInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormPasswordInput'],
  (module) => module.FormPasswordInput as PrimitiveComponentContracts['FormPasswordInput']
);

const FormPhoneInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormPhoneInput'],
  (module) => module.FormPhoneInput
);

const FormRadio = lazyModuleComponent(
  sdkMemberLoaders['Components.FormRadio'],
  (module) => module.FormRadio
);

const FormSelectInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormSelectInput'],
  (module) => module.FormSelectInput
);

const FormSlider = lazyModuleComponent(
  sdkMemberLoaders['Components.FormSlider'],
  (module) => module.FormSlider
);

const FormSwitch = lazyModuleComponent(
  sdkMemberLoaders['Components.FormSwitch'],
  (module) => module.FormSwitch
);

const FormTextArea = lazyModuleComponent(
  sdkMemberLoaders['Components.FormTextArea'],
  (module) => module.FormTextarea as PrimitiveComponentContracts['FormTextArea']
);

const FormTextInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormTextInput'],
  (module) => module.FormTextInput as PrimitiveComponentContracts['FormTextInput']
);

const FormTimeInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormTimeInput'],
  (module) => module.FormTimeInput as PrimitiveComponentContracts['FormTimeInput']
);

const FormTimezoneInput = lazyModuleComponent(
  sdkMemberLoaders['Components.FormTimezoneInput'],
  (module) => module.FormTimezoneInput
);

const HasAnyPermission = lazyModuleComponent(
  sdkMemberLoaders['Components.HasAnyPermission'],
  (module) => module.HasAnyPermission
);

const HasPermission = lazyModuleComponent(
  sdkMemberLoaders['Components.HasPermission'],
  (module) => module.HasPermission
);

const Icon = lazyModuleComponent(sdkMemberLoaders['Components.Icon'], (module) => module.Icon);

const Iframe = lazyModuleComponent(
  sdkMemberLoaders['Components.Iframe'],
  (module) => module.Iframe
);

const KeyValueList = lazyModuleComponent(
  sdkMemberLoaders['Components.KeyValueList'],
  (module) => module.KeyValueList
);

const Link = lazyModuleComponent(sdkMemberLoaders['Components.Link'], (module) => module.Link);

const List = lazyModuleComponent(sdkMemberLoaders['Components.List'], (module) => module.List);

const LoginForm = lazyModuleComponent(
  sdkMemberLoaders['Components.LoginForm'],
  (module) => module.LoginForm
);

const LogoutButton = lazyModuleComponent(
  sdkMemberLoaders['Components.LogoutButton'],
  (module) => module.LogoutButton
);

const Map = lazyModuleComponent(sdkMemberLoaders['Components.Map'], (module) => module.Map);

const Modal = lazyModuleComponent(sdkMemberLoaders['Components.Modal'], (module) => module.Modal);

const NavMain = lazyModuleComponent(
  sdkMemberLoaders['Components.NavMain'],
  (module): PrimitiveComponentContracts['NavMain'] => module.NavMain
);

const NotificationComposer = lazyModuleComponent(
  sdkMemberLoaders['Components.NotificationComposer'],
  (module) => module.NotificationComposer
);

const ScheduledNotificationComposer = lazyModuleComponent(
  sdkMemberLoaders['Components.ScheduledNotificationComposer'],
  (module) => module.ScheduledNotificationComposer
);

const NotificationConsentForm = lazyModuleComponent(
  sdkMemberLoaders['Components.NotificationConsentForm'],
  (module) => module.NotificationConsentForm
);

const NotificationHistory = lazyModuleComponent(
  sdkMemberLoaders['Components.NotificationHistory'],
  (module) => module.NotificationHistory
);

const NotificationVariablesForm = lazyModuleComponent(
  sdkMemberLoaders['Components.NotificationVariablesForm'],
  (module) => module.NotificationVariablesForm
);

const PackageReleaseNotes = lazyModuleComponent(
  sdkMemberLoaders['Components.PackageReleaseNotes'],
  (module) => module.PackageReleaseNotes
);

const PageHeader = lazyModuleComponent(
  sdkMemberLoaders['Components.PageHeader'],
  (module) => module.PageHeader
);

const PaymentForm = lazyModuleComponent(
  sdkMemberLoaders['Components.PaymentForm'],
  (module) => module.PaymentForm
);

const PaymentMethods = lazyModuleComponent(
  sdkMemberLoaders['Components.PaymentMethods'],
  (module) => module.PaymentMethods
);

const Popover = lazyModuleComponent(
  sdkMemberLoaders['Components.Popover'],
  (module) => module.Popover
);

const QrCode = lazyModuleComponent(
  sdkMemberLoaders['Components.QrCode'],
  (module) => module.QrCode
);

const RecordFields = lazyModuleComponent(
  sdkMemberLoaders['Components.RecordFields'],
  (module) => module.RecordFields
);

const RecordScheduleDisplay = lazyModuleComponent(
  sdkMemberLoaders['Components.RecordScheduleDisplay'],
  (module) => module.RecordScheduleDisplay
);

const ResourceTable = lazyModuleComponent(
  sdkMemberLoaders['Components.ResourceTable'],
  (module) => module.ResourceTable
);

const RestoreResourceRecord = lazyModuleComponent(
  sdkMemberLoaders['Components.RestoreResourceRecord'],
  (module) => module.RestoreResourceRecord
);

const RichText = lazyModuleComponent(
  sdkMemberLoaders['Components.RichText'],
  (module) => module.RichText
);

const SearchBar = lazyModuleComponent(
  sdkMemberLoaders['Components.SearchBar'],
  (module): PrimitiveComponentContracts['SearchBar'] => module.SearchBar
);

const Separator = lazyModuleComponent(
  sdkMemberLoaders['Components.Separator'],
  (module): PrimitiveComponentContracts['Separator'] => module.Separator
);

const SharedFilters = lazyModuleComponent(
  sdkMemberLoaders['Components.SharedFilters'],
  (module) => module.SharedFilters
);

const ShowResourceRecord = lazyModuleComponent(
  sdkMemberLoaders['Components.ShowResourceRecord'],
  (module) => module.ShowResourceRecord
);

const Sidebar = lazyModuleComponent(
  sdkMemberLoaders['Components.Sidebar'],
  (module): PrimitiveComponentContracts['Sidebar'] => module.Sidebar
);

const SidebarCollapseButton = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarCollapseButton'],
  (module) => module.SidebarCollapseButton
);

const SidebarContent = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarContent'],
  (module): PrimitiveComponentContracts['SidebarContent'] => module.SidebarContent
);

const SidebarFooter = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarFooter'],
  (module): PrimitiveComponentContracts['SidebarFooter'] => module.SidebarFooter
);

const SidebarHeader = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarHeader'],
  (module): PrimitiveComponentContracts['SidebarHeader'] => module.SidebarHeader
);

const SidebarInset = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarInset'],
  (module): PrimitiveComponentContracts['SidebarInset'] => module.SidebarInset
);

const SidebarMenu = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarMenu'],
  (module): PrimitiveComponentContracts['SidebarMenu'] => module.SidebarMenu
);

const SidebarMenuButton = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarMenuButton'],
  (module): PrimitiveComponentContracts['SidebarMenuButton'] => module.SidebarMenuButton
);

const SidebarMenuItem = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarMenuItem'],
  (module): PrimitiveComponentContracts['SidebarMenuItem'] => module.SidebarMenuItem
);

const SidebarProvider = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarProvider'],
  (module): PrimitiveComponentContracts['SidebarProvider'] => module.SidebarProvider
);

const SidebarTrigger = lazyModuleComponent(
  sdkMemberLoaders['Components.SidebarTrigger'],
  (module): PrimitiveComponentContracts['SidebarTrigger'] => module.SidebarTrigger
);

const SimpleTable = lazyModuleComponent(
  sdkMemberLoaders['Components.SimpleTable'],
  (module) => module.SimpleTable
);

const Skeleton = lazyModuleComponent(
  sdkMemberLoaders['Components.Skeleton'],
  (module): PrimitiveComponentContracts['Skeleton'] => module.Skeleton
);

const Slideover = lazyModuleComponent(
  sdkMemberLoaders['Components.Slideover'],
  (module) => module.Slideover
);

const SlotPicker = lazyModuleComponent(
  sdkMemberLoaders['Components.SlotPicker'],
  (module) => module.SlotPicker
);

// The module page shell already imports `ui/Button`, `ui/Spinner`, and `Image`,
// so they stay eager: a lazy wrapper around a loaded module would only add a
// Suspense frame.
const Button: PrimitiveComponentContracts['Button'] = UiButton;
const Spinner: PrimitiveComponentContracts['Spinner'] = UiSpinner;

const StatCard = lazyModuleComponent(
  sdkMemberLoaders['Components.StatCard'],
  (module) => module.StatCard
);

const SubscriptionBillingPreview = lazyModuleComponent(
  sdkMemberLoaders['Components.SubscriptionBillingPreview'],
  (module) => module.SubscriptionBillingPreview
);

const SubscriptionChangePreview = lazyModuleComponent(
  sdkMemberLoaders['Components.SubscriptionChangePreview'],
  (module) => module.SubscriptionChangePreview
);

const SupportChatButton = lazyModuleComponent(
  sdkMemberLoaders['Components.SupportChatButton'],
  (module) => module.SupportChatButton
);

const Tabs = lazyModuleComponent(sdkMemberLoaders['Components.Tabs'], (module) => module.Tabs);

const ThemeToggle = lazyModuleComponent(
  sdkMemberLoaders['Components.ThemeToggle'],
  (module) => module.ThemeToggle
);

const Timer = lazyModuleComponent(sdkMemberLoaders['Components.Timer'], (module) => module.Timer);

const Tooltip = lazyModuleComponent(
  sdkMemberLoaders['Components.Tooltip'],
  (module) => module.Tooltip
);

const TwoFactorBackupCodeList = lazyModuleComponent(
  sdkMemberLoaders['Components.TwoFactorBackupCodeList'],
  (module) => module.TwoFactorBackupCodeList
);

const TwoFactorBackupCodes = lazyModuleComponent(
  sdkMemberLoaders['Components.TwoFactorBackupCodes'],
  (module) => module.TwoFactorBackupCodes
);

const TwoFactorChallenge = lazyModuleComponent(
  sdkMemberLoaders['Components.TwoFactorChallenge'],
  (module) => module.TwoFactorChallenge
);

const TwoFactorSendCodeButton = lazyModuleComponent(
  sdkMemberLoaders['Components.TwoFactorSendCodeButton'],
  (module) => module.TwoFactorSendCodeButton
);

const TwoFactorSetup = lazyModuleComponent(
  sdkMemberLoaders['Components.TwoFactorSetup'],
  (module) => module.TwoFactorSetup
);

const TwoFactorSmsConsent = lazyModuleComponent(
  sdkMemberLoaders['Components.TwoFactorSmsConsent'],
  (module) => module.TwoFactorSmsConsent
);

const UpdateResourceRecordForm = lazyModuleComponent(
  sdkMemberLoaders['Components.UpdateResourceRecordForm'],
  (module) => module.UpdateResourceRecordForm
);

const UserMenu = lazyModuleComponent(
  sdkMemberLoaders['Components.UserMenu'],
  (module) => module.UserMenu
);

const Video = lazyModuleComponent(sdkMemberLoaders['Components.Video'], (module) => module.Video);

const WorkflowForm = lazyModuleComponent(
  sdkMemberLoaders['Components.WorkflowForm'],
  (module) => module.WorkflowForm
);

const WorkspaceFieldsForm = lazyModuleComponent(
  sdkMemberLoaders['Components.WorkspaceFieldsForm'],
  (module) => module.WorkspaceFieldsForm
);

/**
 * The components that a source factory loads before it renders, so the page
 * shows them in its first frame. A published row-based page loads each one
 * its source names, and its document preloads the same members
 * (`PageSdkMemberChunks::forSourcePage`). The authoring surfaces seed them
 * through `rowRuntime.ts`, so they cost those surfaces no load.
 */
const sourceComponentLoads: Array<{ pattern: RegExp; members: SdkMemberName[] }> = [
  { pattern: /\bCalendar\b/, members: ['Components.Calendar'] },
  {
    pattern:
      /\b(?:CalendarNextButton|CalendarPrevButton|CalendarTitle|CalendarTodayButton|CalendarViewSwitcher)\b/,
    members: [
      'Components.CalendarNextButton',
      'Components.CalendarPrevButton',
      'Components.CalendarTitle',
      'Components.CalendarTodayButton',
      'Components.CalendarViewSwitcher'
    ]
  },
  { pattern: /\bCalendarSelector\b/, members: ['Components.CalendarSelector'] },
  { pattern: /\bDataTable\b/, members: ['Components.DataTable'] },
  { pattern: /\bResourceTable\b/, members: ['Components.ResourceTable'] },
  { pattern: /\bCreateResourceRecordForm\b/, members: ['Components.CreateResourceRecordForm'] },
  { pattern: /\bUpdateResourceRecordForm\b/, members: ['Components.UpdateResourceRecordForm'] },
  { pattern: /\bWorkflowForm\b/, members: ['Components.WorkflowForm'] },
  { pattern: /\bWorkspaceFieldsForm\b/, members: ['Components.WorkspaceFieldsForm'] },
  // One entry for each component that `rowRuntime.ts` seeds, so a published
  // page loads only the ones its source names.
  ...(
    [
      'Components.Accordion',
      'Components.AdminNavButtons',
      'Components.Alert',
      'Components.AnalyticsEmbed',
      'Components.Avatar',
      'Components.Badge',
      'Components.Breadcrumb',
      'Components.ButtonLink',
      'Components.Card',
      'Components.Collapsible',
      'Components.ConfirmDialog',
      'Components.DeleteResourceRecord',
      'Components.DropdownMenu',
      'Components.EmptyState',
      'Components.ErrorBoundary',
      'Components.ForgotPasswordForm',
      'Components.HasAnyPermission',
      'Components.HasPermission',
      'Components.Icon',
      'Components.KeyValueList',
      'Components.Link',
      'Components.List',
      'Components.LoginForm',
      'Components.LogoutButton',
      'Components.Modal',
      'Components.PageHeader',
      'Components.Popover',
      'Components.RestoreResourceRecord',
      'Components.SearchBar',
      'Components.Separator',
      'Components.ShowResourceRecord',
      'Components.SimpleTable',
      'Components.Skeleton',
      'Components.Slideover',
      'Components.StatCard',
      'Components.Tabs',
      'Components.ThemeToggle',
      'Components.Tooltip',
      'Components.UserMenu'
    ] as const
  ).map((member) => ({
    pattern: new RegExp(`\\b${member.slice('Components.'.length)}\\b`),
    members: [member]
  }))
];

export function prepareLazyComponentsForSource(
  sources: Array<string | null | undefined>,
  load: RuntimeLoad
): Promise<unknown> | null {
  const loading = sourceComponentLoads
    .filter(({ pattern }) => sources.some((source) => source && pattern.test(source)))
    .flatMap(({ members }) => members)
    .map((member) => load(sdkMemberLoaders[member]))
    .filter((memberLoad): memberLoad is Promise<unknown> => memberLoad !== null);

  return loading.length > 0 ? Promise.all(loading) : null;
}

export const componentRegistry = {
  Accordion,
  AdminNavButtons,
  AiAgentButton,
  Alert,
  AnalyticsEmbed,
  Avatar,
  Badge,
  BarChart,
  Breadcrumb,
  Button,
  ButtonLink,
  Calendar,
  CalendarConnections,
  CalendarNextButton,
  CalendarPrevButton,
  CalendarSelector,
  CalendarTitle,
  CalendarTodayButton,
  CalendarViewSwitcher,
  Card,
  Collapsible,
  ConfirmDialog,
  CreateResourceRecordForm,
  DataTable,
  DataTableAddFilter,
  DataTableBulkActions,
  DataTableColumnToggle,
  DataTableContent,
  DataTableFilterChips,
  DataTableFilters,
  DataTablePagination,
  DataTableRowActions,
  DataTableSearch,
  DataTableToolbar,
  DeleteResourceRecord,
  DropdownMenu,
  EmailComposer,
  EmailMessages,
  EmptyState,
  ErrorBoundary,
  ForgotPasswordForm,
  FormFieldDisplay,
  FormFieldInput,
  FormCheckbox,
  FormCheckboxGroupInput,
  FormCodeInput,
  FormColorInput,
  FormComboboxInput,
  FormContentInput,
  FormDateInput,
  FormDatetimeInput,
  FormFileUpload,
  FormLocationInput,
  FormMarkdownInput,
  FormMoneyInput,
  FormMultiComboboxInput,
  FormNumberInput,
  FormPasswordInput,
  FormPhoneInput,
  FormRadio,
  FormSelectInput,
  FormSlider,
  FormSwitch,
  FormTextArea,
  FormTextInput,
  FormTimeInput,
  FormTimezoneInput,
  HasAnyPermission,
  HasPermission,
  Icon,
  Iframe,
  Image,
  KeyValueList,
  Link,
  LineChart,
  List,
  LoginForm,
  LogoutButton,
  Map,
  Modal,
  NavMain,
  NotificationComposer,
  NotificationConsentForm,
  NotificationHistory,
  NotificationVariablesForm,
  PackageReleaseNotes,
  PageHeader,
  PaymentForm,
  PaymentMethods,
  PieChart,
  Popover,
  QrCode,
  RecordFields,
  RecordScheduleDisplay,
  ResourceTable,
  RestoreResourceRecord,
  RichText,
  ScheduledNotificationComposer,
  SearchBar,
  Separator,
  SharedFilters,
  ShowResourceRecord,
  Sidebar,
  SidebarCollapseButton,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SimpleTable,
  Skeleton,
  Slideover,
  SlotPicker,
  Spinner,
  StatCard,
  Step,
  Stepper,
  SubscriptionBillingPreview,
  SubscriptionChangePreview,
  SupportChatButton,
  Tabs,
  ThemeToggle,
  Timer,
  Tooltip,
  TwoFactorBackupCodeList,
  TwoFactorBackupCodes,
  TwoFactorChallenge,
  TwoFactorSendCodeButton,
  TwoFactorSetup,
  TwoFactorSmsConsent,
  UpdateResourceRecordForm,
  UserMenu,
  Video,
  WorkflowForm,
  WorkspaceFieldsForm
};

export type ComponentRegistry = typeof componentRegistry;
